import {
	APP_IDENTIFIERS,
	FACEBOOK,
	FILE_TYPE,
	FORM_DETAILS_PAGE_TABS_SEARCH_PARAMS_KEYS,
	HOME_PAGE_TABS,
	HOME_PAGE_TABS_SEARCH_PARAMS_KEYS,
	INSTAGRAM,
	IPHONE,
	LOWERCASE_ALPHANUMERIC,
	type TFileTypeId,
} from '@utils/constants';
import LZString from 'lz-string';

import { ReadonlyURLSearchParams } from 'next/navigation';

import { Workspace } from '@/features/user-settings/types/workspace-types';

import { APP_MENU_ITEMS, APP_ROUTES } from '@/shared/constants/navigation';
import { QUERY_PARAMS } from '@/shared/constants/query-params';
import { CUSTOM_STAGES } from '@/shared/constants/stage';
import { ISFormInputField } from '@/shared/services/forms/forms.types';

export type TDeviceType = 'MOBILE' | 'DESKTOP';

/**
 * Analyzes window dimensions to determine device characteristics
 * Calculates responsive breakpoints and device type based on width thresholds:
 * - Mobile: <= 650px
 * - Tablet: 651px - 1024px
 * - Desktop: > 1024px
 * @param windowHeight - Current window height in pixels
 * @param windowWidth - Current window width in pixels
 * @returns Object containing device type, dimensions and responsive flags
 */
/** Widths at or below this get the mobile experience. */
export const MOBILE_BREAKPOINT_PX = 650;
/** Widths at or below this get the tablet or mobile experience. */
export const TABLET_BREAKPOINT_PX = 1024;

export function getDeviceDimensionsInfo(windowHeight: number, windowWidth: number) {
	const isMobile = windowWidth <= MOBILE_BREAKPOINT_PX;
	const isTablet = windowWidth > MOBILE_BREAKPOINT_PX && windowWidth <= TABLET_BREAKPOINT_PX;
	const isDesktop = windowWidth > TABLET_BREAKPOINT_PX;
	const height = windowHeight;
	const width = windowWidth;
	const deviceType: TDeviceType = isMobile ? 'MOBILE' : 'DESKTOP';

	return {
		isMobile,
		isTablet,
		isDesktop,
		height,
		width,
		deviceType,
	};
}

/**
 * Detects problematic in-app browsers that may cause compatibility issues
 * Specifically checks for:
 * - LinkedIn/Twitter webviews on iPhone
 * - Facebook mobile browser
 * - Instagram in-app browser (including iOS 17 edge case)
 * Used to show warning modal to users on unsupported browsers
 * @param isDesktop - Whether the current device is desktop
 * @returns True if problematic webview is detected, false otherwise
 */
export function shouldShowWebviewErrorModal(isDesktop: boolean) {
	if (typeof window === 'undefined') return true;

	const userAgent = window.navigator.userAgent.toLocaleLowerCase();

	const isIphoneWithLinkedInOrTwitter =
		userAgent.includes(IPHONE) &&
		APP_IDENTIFIERS.some((identifier) => userAgent.includes(identifier));

	const isFacebook = document.referrer.toLocaleLowerCase().includes(FACEBOOK) && !isDesktop;

	const instagramEdgeCase =
		userAgent.includes(IPHONE) && userAgent.includes('os 17_') && !userAgent.includes('safari');

	const isInstagram = userAgent.includes(INSTAGRAM) || instagramEdgeCase;

	return isIphoneWithLinkedInOrTwitter || isFacebook || isInstagram;
}

/**
 * Safely extracts the host value from request headers
 * Primarily used for server-side operations to determine the incoming request's host
 * Falls back to empty string if x-forwarded-host header is not present
 * @param headers - HTTP request headers object
 * @returns Host string from x-forwarded-host header
 */
export function getHostFromHeaders(headers: Headers) {
	return headers.get('x-forwarded-host') ?? '';
}

/** Hand-edited URLs can hold malformed escapes (e.g. `100%`); keep the raw value instead of throwing. */
export function safeDecodeURIComponent(value: string) {
	try {
		return decodeURIComponent(value);
	} catch {
		return value;
	}
}

/**
 * Builds complete domain URL from request headers
 * Combines protocol (https/http) with host to create full domain
 * Used for generating absolute URLs in server-side contexts
 * @param headers - HTTP request headers object
 * @returns Complete domain URL (e.g. "https://example.com")
 */
export function getDomainFromHeaders(headers: Headers) {
	const host = getHostFromHeaders(headers);
	const protocol = headers.get('x-forwarded-proto') ?? 'http';
	return `${protocol}://${host}`;
}

export function getDashboardMenu({ selectedWorkspace }: { selectedWorkspace?: Workspace }) {
	const menu: { name: string; path: string }[] = [];
	const isReferrer = selectedWorkspace?.currentUserRole === 'REFERRER';

	if (!isReferrer) {
		menu.push({
			name: APP_MENU_ITEMS.HOME,
			path: APP_ROUTES.ROOT,
		});

		menu.push({
			name: APP_MENU_ITEMS.MESSAGES,
			path: APP_ROUTES.MESSAGES,
		});
	}

	// Shown to members too — the page itself renders locked for them.
	if (!isReferrer && selectedWorkspace?.id) {
		menu.push({
			name: APP_MENU_ITEMS.CANDIDATES,
			path: APP_ROUTES.CANDIDATES_PAGE,
		});
	}

	if (!isReferrer && selectedWorkspace) {
		menu.push({
			name: APP_MENU_ITEMS.ANALYTICS,
			path: APP_ROUTES.ANALYTICS,
		});
	}

	if (selectedWorkspace) {
		menu.push({
			name: APP_MENU_ITEMS.REFERRALS,
			path: APP_ROUTES.REFERRALS,
		});
	}

	if (!isReferrer && selectedWorkspace) {
		menu.push({
			name: APP_MENU_ITEMS.AI_AGENT,
			path: APP_ROUTES.AI_AGENT,
		});
	}

	if (menu.length === 0) {
		return [];
	}

	return menu;
}

export function getFormDetailsSearchParams(searchParamsFromReq: ReadonlyURLSearchParams) {
	const searchParamsToReturn = new URLSearchParams({});

	searchParamsFromReq.forEach((value, key) => {
		if (FORM_DETAILS_PAGE_TABS_SEARCH_PARAMS_KEYS.includes(key as QUERY_PARAMS)) {
			searchParamsToReturn.set(key, value);
		}
	});

	if (searchParamsToReturn.size === 0) {
		searchParamsToReturn.set(QUERY_PARAMS.TAB, encodeURIComponent(CUSTOM_STAGES.NEW));
	}

	return searchParamsToReturn;
}

/**
 * Processes and validates home page URL parameters
 * Similar to form details validation but for home page:
 * - Filters for allowed parameter keys
 * - Validates tab parameter against allowed values
 * - Ensures default tab parameter if none exists
 * @param searchParamsFromReq - URL search parameters to validate
 * @returns Sanitized URLSearchParams with valid parameters only
 */
export function getValidHomeSearchParams(searchParamsFromReq: ReadonlyURLSearchParams) {
	const searchParamsToReturn = new URLSearchParams();

	searchParamsFromReq.forEach((value, key) => {
		if (HOME_PAGE_TABS_SEARCH_PARAMS_KEYS.includes(key)) {
			const decodedValue = decodeURIComponent(value);

			if (key === QUERY_PARAMS.TAB && !HOME_PAGE_TABS.includes(decodedValue)) {
				searchParamsToReturn.set(key, encodeURIComponent(HOME_PAGE_TABS[0]));
			} else {
				searchParamsToReturn.set(key, value);
			}
		}
	});

	if (!searchParamsToReturn.has(QUERY_PARAMS.TAB)) {
		searchParamsToReturn.set(QUERY_PARAMS.TAB, encodeURIComponent(HOME_PAGE_TABS[0]));
	}

	return searchParamsToReturn;
}

/**
 * Performs deep comparison of two URLSearchParams objects
 * Converts both objects to strings for exact equality check
 * Useful for detecting URL parameter changes in React effects
 * @param params1 - First URLSearchParams object to compare
 * @param params2 - Second URLSearchParams object to compare
 * @returns True if parameters are exactly equal, false otherwise
 */
export function areURLSearchParamsEqual(
	params1: URLSearchParams,
	params2: URLSearchParams
): boolean {
	return params1.toString() === params2.toString();
}

/**
 * Retrieves current URL path with query parameters
 * Strips domain/origin from full URL to get relative path
 * Used for client-side routing and navigation state
 * @returns Current path with search parameters (e.g. "/page?param=value")
 */
export function getCurrentPathWithSearchParams() {
	return window.location.href.replace(window.location.origin, '');
}

/**
 * Compares two string arrays for equality regardless of order
 * Sorts arrays before comparison to handle different orderings
 * Useful for comparing sets of values where order doesn't matter
 * @param arr1 - First array of strings to compare
 * @param arr2 - Second array of strings to compare
 * @returns True if arrays contain same strings, false otherwise
 */
export function sortRowsWithArrOfStrings(arr1: string[], arr2: string[]) {
	return arr1.sort().join('') === arr2.sort().join('');
}

/**
 * Serializes object to compressed URL-safe string
 * Uses LZ-string compression to minimize URL length
 * Useful for storing complex objects in URL parameters
 * @param obj - Any serializable object to compress
 * @returns URL-safe compressed string representation
 */
export function objToEncodedUrlParamString<T>(obj: T): string {
	return LZString.compressToEncodedURIComponent(JSON.stringify(obj));
}

/**
 * Deserializes compressed URL string back to original object
 * Reverses objToEncodedUrlParamString compression
 * Handles parsing errors by throwing exception
 * @param param - Compressed URL string to decompress
 * @returns Original object of type T
 */
export function objFromEncodedUrlParamString<T>(param: string): T {
	try {
		return JSON.parse(LZString.decompressFromEncodedURIComponent(param));
	} catch (error) {
		return {} as T;
	}
}

/**
 * Creates a debounced version of any function
 * Delays execution until specified time has passed since last call
 * Useful for rate-limiting expensive operations (e.g. API calls, DOM updates)
 * @param fn - Function to debounce
 * @param delay - Time in ms to wait before executing
 * @returns Debounced function that maintains original context
 */
export function debounce(fn: (...args: unknown[]) => unknown, delay: number) {
	let timeOutId: NodeJS.Timeout | undefined;
	let pendingCall: (() => void) | undefined;

	const runPendingCall = () => {
		clearTimeout(timeOutId);
		const call = pendingCall;
		pendingCall = undefined;
		call?.();
	};

	const debounced = function (this: unknown, ...args: unknown[]) {
		clearTimeout(timeOutId);
		pendingCall = () => fn.apply(this, args);
		timeOutId = setTimeout(runPendingCall, delay);
	};
	/** Runs the pending call now instead of after the delay; no-op if nothing is pending. */
	debounced.flush = runPendingCall;

	return debounced;
}

/**
 * Intelligently extracts first name from full name string
 * Handles special cases:
 * - Names with initials (e.g. "J. Smith")
 * - Very short names (2 chars or less)
 * - Takes first two parts if first part is too short
 * @param name - Full name string to process
 * @returns Formatted first name or first two name parts
 */
export function getFormattedFirstName(name: string): string {
	const nameParts = name.split(' ');

	let firstName = nameParts[0];

	if (firstName.length <= 2 || firstName.includes('.')) {
		firstName = nameParts.slice(0, 2).join(' ');
	}

	return firstName;
}

/**
 * Tests if a path matches a route pattern with parameters
 * Converts route patterns (e.g. "/user/:id") to regex
 * Useful for route matching in client-side navigation
 * @param routePath - Route pattern with parameter placeholders
 * @param currentPath - Actual path to test against pattern
 * @returns True if path matches pattern, false otherwise
 */
export function matchesRoute(routePath: string, currentPath: string) {
	// Convert route pattern to regex
	const pattern = routePath.replace(/:[^/]+/g, '[^/]+');
	const regex = new RegExp(`^${pattern}$`);
	return regex.test(currentPath);
}

/**
 * Route utility functions for generating various page paths
 */
export function getFormDetailsPath(formId: string) {
	return APP_ROUTES.FORM_DETAILS.replace(':id', formId);
}

export function getFormEditPath(formId: string) {
	return APP_ROUTES.FORM_EDIT.replace(':id', formId);
}

export function getFormFillPath(formId: string) {
	return APP_ROUTES.FORM_FILL.replace(':id', formId);
}

export function getTemplatePath(templateId: string) {
	return APP_ROUTES.TEMPLATE.replace(':id', templateId);
}

/**
 * Creates unique field identifier by combining form ID with random string
 * Ensures uniqueness across form fields
 * Used when generating new form input fields
 * @param formIdentifier - Base form identifier
 * @returns Combined unique field identifier
 */
export function generateFieldId(formIdentifier: string) {
	const randomSuffix = generate4CharRandomString();
	return `${formIdentifier}_${randomSuffix}`;
}

/**
 * Generates random 4-character string for unique identifiers
 * Uses lowercase alphanumeric characters only
 * Provides 36^4 possible combinations
 * @returns Random 4-character string
 */
export function generate4CharRandomString() {
	let result = '';
	for (let i = 0; i < 4; i++) {
		const randomIndex = Math.floor(Math.random() * LOWERCASE_ALPHANUMERIC.length);
		result += LOWERCASE_ALPHANUMERIC.charAt(randomIndex);
	}
	return result;
}

type IGenerateFormInputFieldFunctionParams = Omit<ISFormInputField, 'id'> & {
	formId?: string;
	existingId?: string;
};

/**
 * Creates complete form input field object with all properties
 * Handles optional fields and generates ID if needed
 * Used when creating or updating form fields
 * @param params - Object containing all field properties
 * @returns Complete form input field object
 */
export function getFormInputFieldObject({
	formId,
	existingId,
	type,
	question = '',
	required = false,
	description,
	preDefinedInputType,
	options,
	fileTypes,
	valid = true,
}: IGenerateFormInputFieldFunctionParams): ISFormInputField {
	const obj: ISFormInputField = {
		id: existingId ?? (formId ? generateFieldId(formId) : ''),
		type,
		question,
		required,
	};

	if (description) {
		obj.description = description;
	}

	if (preDefinedInputType) {
		obj.preDefinedInputType = preDefinedInputType;
	}

	if (options) {
		obj.options = options;
	}

	if (fileTypes) {
		obj.fileTypes = fileTypes;
	}

	if (valid) {
		obj.valid = valid;
	}

	return obj;
}

/**
 * Converts File/Blob to Uint8Array for processing
 * Handles conversion errors with clear error message
 * Used in file upload and processing workflows
 * @param file - File or Blob to convert
 * @returns Promise resolving to Uint8Array representation
 */
export async function parseFileToArrayBuffer(file: Blob) {
	try {
		const buffer = await file.arrayBuffer();
		const array = new Uint8Array(buffer);
		return array;
	} catch (error) {
		throw new Error('Error parsing file to ArrayBuffer');
	}
}

/**
 * Reconstructs File object from ArrayBuffer data
 * Preserves original file metadata (name, type, lastModified)
 * Used when processing files need to be converted back
 * @param file - Original file to get metadata from
 * @returns Promise resolving to new File object
 */
export async function convertArrayBufferToFile(file: File): Promise<File> {
	try {
		const imgBuffer = await parseFileToArrayBuffer(file);

		// Create a Blob from the ArrayBuffer
		const blob = new Blob([imgBuffer]);

		// Convert Blob to File
		const newFile = new File([blob], `${file.name}`, {
			type: file.type,
			lastModified: file.lastModified,
		});

		return newFile;
	} catch (error) {
		throw new Error('Error converting ArrayBuffer to File');
	}
}

/**
 * Builds accept attribute string for file input elements
 * Maps internal file type IDs to MIME types
 * Falls back to PDF/JPEG/PNG if no types specified
 * @param fileTypes - Array of allowed file type identifiers
 * @returns Comma-separated string of MIME types
 */
export function getFileInputAcceptTypesString(fileTypes: string[]) {
	const acceptFileString = fileTypes.reduce((acc, fileType) => {
		const fileTypeString = FILE_TYPE[fileType as TFileTypeId];

		return fileTypeString ? `${acc},${fileTypeString}` : acc;
	}, '');

	const fallbackAcceptFileString = `${FILE_TYPE.PDF},${FILE_TYPE.JPEG},${FILE_TYPE.PNG}`;

	return !!acceptFileString ? acceptFileString : fallbackAcceptFileString;
}

/**
 * Validates if URL belongs to specified domain
 * Handles various URL formats:
 * - With/without protocol
 * - With/without subdomain (www, in, m, mobile, ...)
 * - With/without a path after the domain
 * @param urlString - URL to validate
 * @param domain - Domain to check against (e.g. "example.com")
 * @returns True if URL matches domain, false otherwise
 */
export function checkIfUrlIsForParticularDomain(urlString: string, domain: string) {
	const isValidUrl = checkIfUrlIsValid(urlString);

	if (!isValidUrl) {
		return false;
	}

	const escapedDomain = domain.replace(/\./g, '\\.');

	const regex = new RegExp('^(https?://)?([\\w-]+\\.)*' + escapedDomain + '(/[^\\s]*)?$', 'i');

	return regex.test(urlString);
}

/**
 * Validates URL string format
 * Checks for:
 * - Optional protocol (http/https)
 * - Valid domain structure
 * - Optional port
 * - Optional path
 * @param urlString - URL string to validate
 * @returns True if URL format is valid, false otherwise
 */
export function checkIfUrlIsValid(urlString: string) {
	const regex = /^(https?:\/\/)?(([\w-]+\.)+)[a-zA-Z]{2,}(:\d+)?(\/[^\s<>{}|\\^`]*[^\s<>]*)?$/i;

	return regex.test(urlString);
}

/**
 * Creates new option object with unique identifier
 * Generates ID by combining name with timestamp
 * Used for creating form field options
 * @param optionName - Display name for the option
 * @returns Option object with unique ID and name
 */
export function getNewOptionObject(optionName: string) {
	const newOptionId = optionName.replace(/\s+/g, '-') + new Date().getTime().toString();

	return {
		id: newOptionId,
		name: optionName,
	};
}

/**
 * Checks if the current device is running on a Mac operating system
 * Uses navigator.userAgent to detect Mac OS presence
 * @returns True if Mac OS is detected, false otherwise
 */
export function getIsMacOs() {
	if (typeof navigator === 'undefined') return false;
	return /Macintosh/i.test(navigator.userAgent);
}
